import type Rollbar from "rollbar";

const aggregationWindowMs = 60 * 1000;

interface Incident {
	startedAt: number;
	failureCount: number;
	failingQueries: Record<string, number>;
	recoveredAt: number | null;
}

let incident: Incident | null = null;

const objectIdPattern = /^[0-9a-f]{24}$/;
const isoDatePattern = /^\d{4}-\d{2}-\d{2}T/;

/**
 * Collapses a query key to a groupable label by keeping its named string
 * segments and dropping ids and dates, e.g.
 * ["user", "66…", "account", "54…", "alerts", […], "after", "2026-…"]
 * becomes "user.account.alerts.after".
 */
const toLabel = (queryKey: readonly unknown[]) =>
	queryKey
		.filter(
			(segment): segment is string =>
				typeof segment === "string" &&
				!objectIdPattern.test(segment) &&
				!isoDatePattern.test(segment)
		)
		.join(".") || "unknown";

/**
 * Records a network-level query failure. The first failure opens an incident
 * and schedules a single aggregated rollbar.warning for one aggregation
 * window later; failures in the meantime only increment counters, so a
 * flood of failing requests costs one Rollbar occurrence per window.
 */
export const trackNetworkFailure = (
	rollbar: Rollbar,
	queryKey: readonly unknown[]
) => {
	if (!incident) {
		incident = {
			startedAt: Date.now(),
			failureCount: 0,
			failingQueries: {},
			recoveredAt: null,
		};
		setTimeout(() => flush(rollbar), aggregationWindowMs);
	}

	const label = toLabel(queryKey);
	incident.failingQueries[label] = (incident.failingQueries[label] ?? 0) + 1;
	incident.failureCount += 1;
	incident.recoveredAt = null;
};

/**
 * Records that a query succeeded, marking the open incident (if any) as
 * recovered. Called on every query success; the recovery only holds if no
 * further failure follows it before the window closes.
 */
export const trackNetworkRecovery = () => {
	if (incident && incident.recoveredAt === null) {
		incident.recoveredAt = Date.now();
	}
};

const flush = (rollbar: Rollbar) => {
	if (!incident) return;

	rollbar.warning("Network incident", {
		startedAt: new Date(incident.startedAt).toISOString(),
		windowMs: aggregationWindowMs,
		failureCount: incident.failureCount,
		failingQueries: incident.failingQueries,
		recovered: incident.recoveredAt !== null,
		msToRecovery:
			incident.recoveredAt === null ?
				null
			:	incident.recoveredAt - incident.startedAt,
	});

	incident = null;
};
